import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { LatestInsights } from '../../../shared/latest-insights/latest-insights';
import { DevFlag } from '../../../shared/dev-flag/dev-flag';
import {
  Subject,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../core/api/api.config';
import {
  ApiPagination,
  FrontendCatalogueTotals,
  FrontendStore,
  FrontendStoreAlphabetSection,
  FrontendStoreAlphabetSectionStore,
} from '../../../core/api/api.models';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import {
  BreadcrumbCrumb,
  breadcrumbSchemaFor,
  breadcrumbTrail,
} from '../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';
import { StoreLogo } from '../../../shared/store-logo/store-logo';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const NUMBER_GROUP = '0-9';
const FEATURED_LOGO_LIMIT = 8;
const TEXT_STORE_LIMIT = 19;
// The main /storelist page shows at most this many stores per alphabet section
// (8 featured logo cards + 19 text cards), so each section fetches only this slice.
const SECTION_VISIBLE_LIMIT = FEATURED_LOGO_LIMIT + TEXT_STORE_LIMIT;
const LETTER_PAGE_LIMIT = 100;
const MAIN_SEARCH_LIMIT = 100;

interface AllStoreItem {
  id: string;
  name: string;
  slug: string;
  logoUrl: string;
  logoInitials: string;
  couponsAndDealsCount: string;
  path: string;
}

interface AlphabetNavItem {
  label: string;
  value: string;
  path: string;
}

interface FeaturedLetterSection {
  label: string;
  value: string;
  heading: string;
  featuredStores: AllStoreItem[];
  textOnlyStores: AllStoreItem[];
  viewMorePath: string;
  totalStores: number;
  showViewMore: boolean;
  isLoading: boolean;
}

@Component({
  selector: 'app-allstores-page',
  imports: [Breadcrumb, RouterLink, LatestInsights, DevFlag, StoreLogo],
  templateUrl: './allstores-page.html',
  styleUrl: './allstores-page.scss',
})
export class AllstoresPage {
  /** Set alongside the SEO tags, since both depend on which letter (if any) is being shown. */
  protected readonly crumbs = signal<BreadcrumbCrumb[]>(breadcrumbTrail({ label: 'Stores' }));

  private readonly api = inject(SavingArenaApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly seo = inject(SeoService);
  private readonly searchInput$ = new Subject<string>();

  protected readonly alphabetNavItems: AlphabetNavItem[] = [
    { label: '( 0 - 9 )', value: NUMBER_GROUP, path: `/storelist/${NUMBER_GROUP}` },
    ...ALPHABET.map((letter) => ({
      label: letter,
      value: letter,
      path: `/storelist/${letter.toLowerCase()}`,
    })),
  ];

  protected readonly featuredLetterSections = signal<FeaturedLetterSection[]>([]);
  protected readonly mainSearchStores = signal<AllStoreItem[]>([]);
  protected readonly mainSearchTotal = signal<number | null>(null);
  protected readonly selectedStores = signal<AllStoreItem[]>([]);

  /** Which page of the letter is on screen. 1 is the bare `/storelist/<letter>` URL. */
  protected readonly currentPage = signal(1);
  protected readonly totalPages = signal(1);
  protected readonly selectedStoreTotal = signal<number | null>(null);
  protected readonly isMainPageLoading = signal(false);
  protected readonly isSelectedPageLoading = signal(false);
  protected readonly selectedGroup = signal<string | null>(null);
  protected readonly searchTerm = signal('');

  protected readonly isLetterPage = computed(() => this.selectedGroup() !== null);

  /**
   * The page numbers to render as links: first, last, and a window around the current page, with
   * `null` standing for a gap.
   *
   * Every page is reachable by walking prev/next, so the crawler loses nothing — but a letter with
   * 36 pages would otherwise put 36 links in the footer of every one of them, which is the kind of
   * near-duplicate link block that made this page a problem in the first place.
   */
  protected readonly pageLinks = computed<(number | null)[]>(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    if (total <= 1) return [];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const wanted = new Set([1, total, current, current - 1, current + 1]);
    const shown = [...wanted].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);

    const out: (number | null)[] = [];
    let previous = 0;
    for (const n of shown) {
      if (previous && n - previous > 1) out.push(null);
      out.push(n);
      previous = n;
    }
    return out;
  });

  /** Query params for a page link. Page 1 is the bare URL, so it carries none. */
  protected pageQuery(page: number): Record<string, string> {
    return page <= 1 ? {} : { page: String(page) };
  }
  protected readonly isMainSearchActive = computed(
    () => !this.isLetterPage() && this.searchTerm().trim().length > 0,
  );
  protected readonly selectedHeading = computed(() => {
    const group = this.selectedGroup();

    if (!group) {
      return '';
    }

    return `Featured "${this.groupLabel(group)}" Stores`;
  });
  protected readonly selectedSearchPlaceholder = computed(() => {
    const group = this.selectedGroup();

    if (!group) {
      return 'search all stores by name';
    }

    return `search by store name starts with ${this.groupLabel(group)}`;
  });
  /**
   * Live catalogue sizes for the three counters above the list.
   *
   * `null` until the call lands, so the counters render a dash rather than a number that is about
   * to change under the reader.
   */
  protected readonly catalogueTotals = signal<FrontendCatalogueTotals | null>(null);

  protected readonly storeTotalLabel = computed(() => {
    const selectedTotal = this.selectedStoreTotal();
    const mainSearchTotal = this.mainSearchTotal();

    // A letter page or a search shows the size of THAT result set; the bare list shows the whole
    // catalogue.
    if (selectedTotal !== null) {
      return selectedTotal.toLocaleString();
    }

    if (mainSearchTotal !== null) {
      return mainSearchTotal.toLocaleString();
    }

    const totals = this.catalogueTotals();
    return totals ? totals.stores.toLocaleString() : '—';
  });

  protected readonly activeCouponsLabel = computed(() => {
    const totals = this.catalogueTotals();
    return totals ? totals.coupons.toLocaleString() : '—';
  });

  protected readonly activeProductsLabel = computed(() => {
    const totals = this.catalogueTotals();
    return totals ? totals.products.toLocaleString() : '—';
  });

  constructor() {
    // Catalogue-wide, so it is fetched once rather than per letter or per search.
    this.api
      .getCatalogueTotals()
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (response?.data) {
          this.catalogueTotals.set(response.data);
        }
      });

    // Both, because the letter and the page number each change what is fetched, and a visitor can
    // change either without leaving the route.
    combineLatest([this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([params, query]) => ({
          group: this.normalizeGroup(params.get('letter')),
          page: this.normalizePage(query.get('page')),
        })),
        distinctUntilChanged((a, b) => a.group === b.group && a.page === b.page),
        switchMap(({ group, page }) => {
          this.selectedGroup.set(group);
          this.currentPage.set(page);
          this.applyStoreListSeo(group);
          this.mainSearchStores.set([]);
          this.mainSearchTotal.set(null);
          this.selectedStores.set([]);
          this.selectedStoreTotal.set(null);
          this.searchTerm.set('');

          if (group) {
            return this.searchInput$.pipe(
              debounceTime(250),
              distinctUntilChanged(),
              startWith(''),
              switchMap((searchTerm) => {
                this.isSelectedPageLoading.set(true);
                this.selectedStores.set([]);
                this.selectedStoreTotal.set(null);

                return this.fetchSelectedGroup(group, searchTerm, page).pipe(
                  map((result) => ({ mode: 'selected' as const, result })),
                );
              }),
            );
          }

          return this.searchInput$.pipe(
            debounceTime(250),
            distinctUntilChanged(),
            startWith(''),
            switchMap((searchTerm) => {
              this.isMainPageLoading.set(true);
              this.mainSearchStores.set([]);
              this.mainSearchTotal.set(searchTerm ? null : this.mainSearchTotal());

              if (searchTerm) {
                return this.fetchMainSearch(searchTerm).pipe(
                  map((result) => ({ mode: 'mainSearch' as const, result })),
                );
              }

              this.mainSearchTotal.set(null);
              return this.fetchMainSections().pipe(
                map((sections) => ({ mode: 'main' as const, sections })),
              );
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payload) => {
        if (payload.mode === 'selected') {
          this.selectedStores.set(payload.result.stores);
          this.selectedStoreTotal.set(payload.result.total);
          this.totalPages.set(payload.result.pages);
          this.isSelectedPageLoading.set(false);
          // The page count is only known once the response lands, so the prev/next links and the
          // "page N of M" in the title are applied here rather than with the rest of the SEO.
          this.applyPaginationSeo();
          return;
        }

        if (payload.mode === 'mainSearch') {
          this.mainSearchStores.set(payload.result.stores);
          this.mainSearchTotal.set(payload.result.total);
          this.isMainPageLoading.set(false);
          return;
        }

        this.featuredLetterSections.set(payload.sections);
        this.isMainPageLoading.set(false);
      });
  }

  /**
   * SEO for /storelist and /storelist/:letter. The page set no <title> at all before this.
   *
   * The letter is route data (it resolves with the param map, not with the store request), so this
   * runs as soon as the route emits — well before SSR serialises the DOM. The canonical always
   * carries the letter, so /storelist/a and /storelist stay distinct URLs; an unrecognised letter
   * normalises to `null` and therefore canonicalises back to the un-lettered /storelist.
   *
   * The store counts are deliberately not in the description — they change on every crawl.
   */
  private applyStoreListSeo(group: string | null): void {
    const label = group ? this.groupLabel(group) : '';
    const path = group ? `/storelist/${group.toLowerCase()}` : '/storelist';
    const url = this.seo.absolute(path);

    const title = label
      ? `Stores Starting With ${label}: Coupons, Promo Codes & Deals | SavingArena`
      : 'All Stores A-Z: Coupons, Promo Codes & Deals | SavingArena';
    const description = label
      ? `Every store on SavingArena whose name starts with ${label}, with verified coupon codes, promo codes and deals for each brand.`
      : 'Browse the full SavingArena store directory from A to Z and find verified coupon codes, promo codes and deals for thousands of brands.';

    const page = this.currentPage();

    // Page 2 says so, in the title and the description. Without it every page of a letter carries
    // the same pair, which is the duplicate-title problem this crawl already found elsewhere.
    const suffix = page > 1 ? ` | Page ${page}` : '';

    this.seo.apply({
      title: title.replace(' | SavingArena', `${suffix} | SavingArena`),
      description: page > 1 ? `${description} Page ${page}.` : description,
      canonicalPath: path,
      // Self-referencing, NOT pointing back at page 1. Canonicalising a paginated page to its first
      // page tells Google to drop it, and with it every store that is only linked from there.
      canonicalQuery: page > 1 ? `page=${page}` : '',
    });

    // The same trail the page renders, so the two cannot disagree.
    this.crumbs.set(
      label
        ? breadcrumbTrail({ label: 'Stores', path: '/storelist' }, { label })
        : breadcrumbTrail({ label: 'Stores' }),
    );

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ]);
  }

  /**
   * `rel="prev"` / `rel="next"`, once the response has told us how many pages there are.
   *
   * Page 1's `prev` and the last page's `next` are empty, which clears them — a stale `next` left
   * over from a previous letter would point somewhere unrelated.
   */
  private applyPaginationSeo(): void {
    const group = this.selectedGroup();
    if (!group) {
      this.seo.setPaginationLinks('', '');
      return;
    }

    const base = this.seo.absolute(`/storelist/${group.toLowerCase()}`);
    const page = this.currentPage();
    const total = this.totalPages();

    const at = (n: number) => (n <= 1 ? base : `${base}?page=${n}`);

    this.seo.setPaginationLinks(page > 1 ? at(page - 1) : '', page < total ? at(page + 1) : '');
  }

  /** `?page=` as a whole number, defaulting to 1 for anything that is not one. */
  private normalizePage(value: string | null): number {
    const parsed = Number.parseInt(String(value ?? '1'), 10);
    return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
  }

  protected onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;

    this.searchTerm.set(searchTerm);

    this.searchInput$.next(searchTerm.trim());
  }

  private fetchMainSections() {
    const groups = [NUMBER_GROUP, ...ALPHABET];

    // One request returns every alphabet group's newest stores + accurate totals, computed
    // server-side in a single aggregation. This replaces the previous 27-request fan-out.
    return this.api.getStoreAlphabetSections(SECTION_VISIBLE_LIMIT).pipe(
      map((response) => {
        const sectionsByGroup = new Map(
          response.data.map((section) => [section.group, section]),
        );

        return groups.map((group) => this.toFeaturedSection(group, sectionsByGroup.get(group)));
      }),
      catchError(() => of(groups.map((group) => this.toFeaturedSection(group)))),
    );
  }

  private toFeaturedSection(
    group: string,
    section?: FrontendStoreAlphabetSection,
  ): FeaturedLetterSection {
    const items = (section?.stores ?? []).map((store) => this.toSectionStoreItem(store));
    const featuredStores = items.slice(0, FEATURED_LOGO_LIMIT);
    const textOnlyStores = items.slice(FEATURED_LOGO_LIMIT, SECTION_VISIBLE_LIMIT);
    const total = section?.total ?? 0;

    return {
      label: this.groupLabel(group),
      value: group,
      heading: `Featured "${this.groupLabel(group)}" Stores`,
      featuredStores,
      textOnlyStores,
      viewMorePath: `/storelist/${group.toLowerCase()}`,
      totalStores: total,
      // The section only renders `items`, so "View More" appears whenever the group's true
      // total exceeds what we fetched for display.
      showViewMore: total > featuredStores.length + textOnlyStores.length,
      isLoading: false,
    };
  }

  private fetchMainSearch(searchTerm: string) {
    const normalizedSearchTerm = searchTerm.toLowerCase();

    return this.api
      .getStores({
        search: searchTerm,
        page: 1,
        limit: MAIN_SEARCH_LIMIT,
        sortBy: 'name',
        sortOrder: 'asc',
      })
      .pipe(
        map((response) => {
          const stores = response.data
            .filter((store) => store.name.toLowerCase().includes(normalizedSearchTerm))
            .map((store) => this.toStoreItem(store));

          return { stores, total: response.pagination.total };
        }),
        catchError(() => of({ stores: [], total: 0 })),
      );
  }

  /**
   * Letter pages already fetched in this browsing session.
   *
   * The API answers in 77ms measured on the server, but a visitor is a round trip away — from
   * Pakistan that is close to two seconds, and every one of them was spent staring at "Loading
   * stores…" even when they had just come from that letter. Walking A → G → A now costs one request,
   * not two.
   *
   * Deliberately not persisted. It is a cheap fix for the back-and-forth within one visit; a store
   * list going stale between sessions is the kind of thing a memory cache should not decide.
   */
  private readonly pageCache = new Map<string, { stores: AllStoreItem[]; total: number; pages: number }>();

  /**
   * Starts the fetch when a finger or cursor lands on a letter, before the click.
   *
   * Most of the wait is the round trip, and the moment between touching a link and letting go is
   * enough of it to matter. If the click never comes the response simply sits in `pageCache`, which
   * costs one request; if it does, the data is usually already there.
   */
  protected prefetchGroup(value: string): void {
    const group = this.normalizeGroup(value);
    if (!group || this.pageCache.has(this.cacheKey(group, 1, ''))) return;

    this.fetchSelectedGroup(group, '', 1).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private cacheKey(group: string, page: number, searchTerm: string): string {
    return `${group}|${page}|${searchTerm.trim().toLowerCase()}`;
  }

  /**
   * One page of a letter, not the whole letter.
   *
   * This used to read `pagination.pages` off the first response and then fetch every remaining page
   * — 36 requests for "S", whose 3,511 stores all landed in one document. Angular serialises each
   * response into the HTML for hydration, so `/storelist/s` came to 4.25 MB of which 2.94 MB was
   * that transfer state, rendered 3,511 links, and took 11 to 15 seconds. No visitor reads 3,511
   * links and no crawler needs them on one page.
   */
  private fetchSelectedGroup(group: string, searchTerm: string, page: number) {
    const key = this.cacheKey(group, page, searchTerm);
    const cached = this.pageCache.get(key);
    if (cached) return of(cached);

    return this.fetchSelectedGroupPage(group, page, searchTerm).pipe(
      map((response) => {
        const normalizedSearchTerm = searchTerm.toLowerCase();
        const stores = this.sortStores(
          this.uniqueStores(response.data).filter(
            (store) =>
              this.matchesGroup(store.name, group) &&
              (!normalizedSearchTerm || store.name.toLowerCase().includes(normalizedSearchTerm)),
          ),
          'name',
        ).map((store) => this.toStoreItem(store));

        const result = {
          stores,
          total: response.pagination.total,
          pages: Math.max(response.pagination.pages, 1),
        };

        // Only a real answer is remembered; a failed request falls through to the catch below and is
        // not cached, so a hiccup does not pin an empty list to that letter for the rest of the visit.
        this.pageCache.set(key, result);
        return result;
      }),
      catchError(() => of({ stores: [], total: 0, pages: 1 })),
    );
  }

  private fetchSelectedGroupPage(group: string, page: number, searchTerm: string) {
    return this.api
      .getStores({
        startsWith: group.toLowerCase(),
        search: searchTerm || undefined,
        page,
        limit: LETTER_PAGE_LIMIT,
        sortBy: 'name',
        sortOrder: 'asc',
      })
      .pipe(catchError(() => of({ success: false, data: [], pagination: this.emptyPagination() })));
  }

  private toSectionStoreItem(store: FrontendStoreAlphabetSectionStore): AllStoreItem {
    return {
      id: store.id,
      name: store.name,
      slug: store.slug,
      logoUrl: this.assetUrl(store.logoImagePath),
      logoInitials: this.initials(store.name),
      couponsAndDealsCount: 'View Coupons & Deals',
      path: `/${store.slug}`,
    };
  }

  private toStoreItem(store: FrontendStore): AllStoreItem {
    return {
      id: store.id,
      name: store.name,
      slug: store.slug,
      logoUrl: this.assetUrl(store.logoImagePath),
      logoInitials: this.initials(store.name),
      couponsAndDealsCount: this.couponCountText(store),
      path: `/${store.slug}`,
    };
  }

  private couponCountText(store: FrontendStore): string {
    const coupons = Array.isArray(store.coupons) ? store.coupons.length : 0;

    if (coupons > 0) {
      return `${coupons}+ Coupons & Deals`;
    }

    return 'View Coupons & Deals';
  }

  private assetUrl(path: string | undefined): string {
    if (!path) {
      return '';
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    return `${this.apiConfig.assetBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private initials(name: string): string {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  private uniqueStores(stores: FrontendStore[]): FrontendStore[] {
    const seenIds = new Set<string>();

    return stores.filter((store) => {
      if (seenIds.has(store.id)) {
        return false;
      }

      seenIds.add(store.id);
      return true;
    });
  }

  private sortStores(stores: FrontendStore[], sortBy: 'createdAt' | 'name'): FrontendStore[] {
    return [...stores].sort((first, second) => {
      if (sortBy === 'name') {
        return first.name.localeCompare(second.name);
      }

      return this.storeDateValue(second) - this.storeDateValue(first);
    });
  }

  private storeDateValue(store: FrontendStore): number {
    const createdAt = store['createdAt'];
    const date = typeof createdAt === 'string' ? createdAt : store.updatedAt;

    return date ? Date.parse(date) || 0 : 0;
  }

  private matchesGroup(name: string, group: string): boolean {
    const firstCharacter = name.trim().charAt(0).toUpperCase();

    if (!firstCharacter) {
      return false;
    }

    if (group === NUMBER_GROUP) {
      return /^[0-9]$/.test(firstCharacter);
    }

    return firstCharacter === group;
  }

  private normalizeGroup(group: string | null): string | null {
    if (!group) {
      return null;
    }

    const normalized = group.toLowerCase();

    if (normalized === NUMBER_GROUP) {
      return NUMBER_GROUP;
    }

    if (/^[a-z]$/.test(normalized)) {
      return normalized.toUpperCase();
    }

    return null;
  }

  private groupLabel(group: string): string {
    return group === NUMBER_GROUP ? '0-9' : group;
  }

  private emptyPagination(): ApiPagination {
    return { page: 1, limit: LETTER_PAGE_LIMIT, total: 0, pages: 0 };
  }
}
